import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useTheme } from '@evi/skins';
import { MapContext } from './useMap';
import { DASHBOARD_MAP_GESTURES } from './mapGestures';

const MAPBOX_TOKEN = import.meta.env['VITE_MAPBOX_TOKEN'] as string | undefined;

/**
 * Continent-wide overview camera. Admin operators may have
 * locations spread across regions, so we open zoomed out
 * (north-america-fits-in-the-viewport) rather than pinning to
 * a city. Per-location overrides can pass their own camera
 * targets via the `location.cameraTarget` field on the data
 * model (currently unset; CameraController falls through to a
 * sensible default for any location it can't find an override
 * for).
 */
export const OVERVIEW_CAMERA = {
  lng: -98.5, // continental US centroid (Kansas-ish)
  // lat dropped from 39.5 (Kansas) to 28 (Gulf coast). With bearing 0
  // (north-up) and pitch 30, anchoring the camera further south
  // extends the looking-distance northward — more continent extends
  // up the frame before the globe curves to the horizon, so the
  // visible globe edge rides higher and the map breathes upward into
  // the empty band between the dashboard's chart and bottom row.
  // CameraController.tsx's reset-to-overview also reads this const,
  // so the same view applies on map-deselect.
  lat: 28,
  zoom: 3.5,
  pitch: 30,
  bearing: 0,
} as const;

/** Tilt drift threshold — if the user manually rotates more
 *  than this many degrees off the locked bearing, we snap back
 *  to the OVERVIEW_CAMERA bearing on `dragend`. Matches eviinfra
 *  pattern; preserves the dashboard's deliberate tilted-globe
 *  look. */
const BEARING_DRIFT_TOLERANCE_DEG = 15;

interface MapContainerProps {
  children?: React.ReactNode;
  /** Optional className for the canvas-host div. Lets the host
   *  widget control border-radius + sizing without the container
   *  baking opinions in. */
  className?: string;
  /** Theme mode — picks the basemap style URL and gates the
   *  Standard-style runtime config (lightPreset / labels) which
   *  is only valid on the Standard family. Defaults to 'dark'. */
  mode?: 'light' | 'dark';
}

/**
 * Fallback per-mode basemap style URLs — used only for themes that
 * declare no `mapStyle` (e.g. Luce). Themed families resolve their
 * own style via `theme.mapStyle` (see `styleUrl` in the component).
 *   dark  → standard-satellite (the v3 satellite scene; supports the
 *           basemap config schema the dusk block tweaks)
 *   light → light-v11 (classic clean light style; no basemap schema,
 *           so the dusk block's URL gate skips it)
 */
const STYLE_URLS = {
  dark: 'mapbox://styles/mapbox/standard-satellite',
  light: 'mapbox://styles/mapbox/light-v11',
} as const;

/**
 * Mounts a mapbox-gl scene + provides it to descendants via
 * `MapContext`. One <MapContainer> per dashboard map widget
 * mount. Style + initial camera are baked in here; runtime
 * customization lives in the `style.load` handler below.
 *
 * Style:   resolved from `theme.mapStyle?.[mode]`, falling back to
 *   STYLE_URLS for themes without one (Luce). When the resolved URL
 *   is standard-satellite it's further configured at runtime via the
 *   v3 `setConfigProperty('basemap', …)` API inside the style.load
 *   block (the dusk globe).
 *
 * Camera lock:
 *   Map renders tilted (pitch 30 + bearing 0) for a globe-y
 *   feel. The user can pan/zoom freely, but a long manual
 *   rotation drift snaps back on dragend so the visual identity
 *   stays consistent. The `flyTo` calls from CameraController
 *   set their own pitch/bearing per destination — we don't
 *   re-lock during a flyTo.
 *
 * Token:
 *   Read from VITE_MAPBOX_TOKEN at build time. If the env is
 *   missing the container renders a fallback message instead
 *   of a blank canvas; widgets descend into their own empty
 *   states downstream.
 */
export function MapContainer({
  children,
  className,
  mode = 'dark',
}: MapContainerProps) {
  // Theme-aware basemap. Resolve the style from the active theme's
  // mapStyle, falling back to STYLE_URLS for themes that declare none
  // (Luce). Read once per mount; a theme/mode flip remounts via the
  // caller's key={`${theme.id}-${mode}`}, so the fresh instance picks
  // up the new style constructor-time (mapbox-gl style is mount-time).
  const { theme } = useTheme();
  const styleUrl = theme.mapStyle?.[mode] ?? STYLE_URLS[mode];
  // Dusk config is only valid on standard-satellite (the v3 basemap
  // schema). Gate on the RESOLVED URL, mirroring the driver's
  // PwaMapContainer — fires for Default dark + Luce dark fallback
  // (both resolve to standard-satellite), never for classic styles.
  const isStandardSatellite = styleUrl === 'mapbox://styles/mapbox/standard-satellite';

  const hostRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [map, setMap] = useState<mapboxgl.Map | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!hostRef.current || !MAPBOX_TOKEN) return;
    // Idempotent — strict-mode double-invoke or remounts during
    // dev HMR mustn't stack maps in the same host.
    if (mapRef.current) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;

    const instance = new mapboxgl.Map({
      container: hostRef.current,
      style: styleUrl,
      center: [OVERVIEW_CAMERA.lng, OVERVIEW_CAMERA.lat],
      zoom: OVERVIEW_CAMERA.zoom,
      pitch: OVERVIEW_CAMERA.pitch,
      bearing: OVERVIEW_CAMERA.bearing,
      minZoom: 2,
      maxZoom: 18,
      attributionControl: false,
      // Free gestures at every width, cooperativeGestures off (see mapGestures.ts). Whether a gesture reaches
      // the map at all is the page's pass-through CSS (DashboardPage.module.css, after .mapZone).
      ...DASHBOARD_MAP_GESTURES,
    });

    mapRef.current = instance;
    setMap(instance);

    // Runtime style config — only valid once style.load fires AND
    // only on the standard-satellite style (the v3 basemap schema).
    // Classic styles (satellite-streets, light-v11, dark-v11,
    // outdoors) don't expose that schema, so calling setConfigProperty
    // on them would throw `Object 'basemap' does not exist in the
    // schema`. Gate on the resolved URL via isStandardSatellite,
    // exactly like the driver's PwaMapContainer.
    if (isStandardSatellite) {
      instance.on('style.load', () => {
        try {
          instance.setConfigProperty('basemap', 'lightPreset', 'dusk');
          instance.setConfigProperty('basemap', 'showPointOfInterestLabels', false);
          instance.setConfigProperty('basemap', 'showTransitLabels', false);
          instance.setConfigProperty('basemap', 'showRoadLabels', false);
        } catch {
          // Style doesn't expose the standard basemap schema —
          // safe to ignore; visual identity degrades gracefully.
        }
      });
    }

    instance.on('load', () => { setIsLoaded(true); });

    // Snap-back on rotation drift. Pitch lock is intentionally
    // omitted — operators occasionally flatten the view to read
    // close-set markers, and forcing it back fights their input.
    instance.on('dragend', () => {
      const bearing = instance.getBearing();
      if (Math.abs(bearing) > BEARING_DRIFT_TOLERANCE_DEG) {
        instance.easeTo({
          bearing: 0,
          duration: 600,
          essential: false, // respects prefers-reduced-motion
        });
      }
    });

    return () => {
      instance.remove();
      mapRef.current = null;
      setMap(null);
      setIsLoaded(false);
    };
  }, []);

  // Defensive: even though the constructor sets cooperativeGestures
  // false, push it explicitly once the map mounts in case the style
  // load or any other init path flips it back. No viewport dep —
  // the value is hardcoded false at every width.
  useEffect(() => {
    if (!map) return;
    map.setCooperativeGestures(false);
  }, [map]);

  if (!MAPBOX_TOKEN) {
    return (
      <div
        className={className}
        style={{
          width: '100%', height: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--evi-text-mute, #888)',
          padding: 16, textAlign: 'center',
          fontFamily: 'var(--font-body)',
          fontSize: 13,
        }}
      >
        Set <code>VITE_MAPBOX_TOKEN</code> in <code>apps/admin/.env</code> to display the map.
      </div>
    );
  }

  return (
    <MapContext.Provider value={{ map, isLoaded }}>
      {/* aria-hidden lives on THIS div only — the mapbox canvas
          host. The map is decorative chrome and shouldn't be
          enumerated by screen readers / keyboard tab order.
          Children mounted as siblings below (markers, camera
          controller, info card) DO stay accessible. The previous
          wrapper-level aria-hidden in DashboardPage.tsx
          accidentally hid the card subtree along with the map. */}
      <div
        ref={hostRef}
        className={className}
        style={{ width: '100%', height: '100%' }}
        role="region"
        aria-label="Charging sites map"
      />
      {/* Children mount after the host div so the Map provider's
          value is already populated when they first render — they
          can also defensively gate on isLoaded if their effect
          requires a loaded style. */}
      {children}
    </MapContext.Provider>
  );
}
